import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Info,
  Loader2,
  Trash2
} from "lucide-react";
import type { FC, ReactNode } from "react";

import { FileTypeIcon } from "@/components/common/FileTypeIcon";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type UploadStatus =
  | "busy"
  | "ready"
  | "warn"
  | "info"
  | "error"
  | "muted";

/** The card's leading badge, which doubles as its status. */
export const UploadStatusIcon: FC<{ status: UploadStatus }> = ({ status }) => {
  switch (status) {
    case "busy":
      return <Loader2 className="size-4 animate-spin text-fg-3" />;
    case "error":
      return <AlertCircle className="size-4 text-danger" />;
    case "muted":
      return <AlertCircle className="size-4 text-fg-3" />;
    case "warn":
      return <AlertTriangle className="size-4 text-warn" />;
    case "info":
      return <Info className="size-4 text-warn" />;
    default:
      return <CheckCircle2 className="size-4 text-success" />;
  }
};

type Props = {
  /** Anchors the preview's pop-open origin (`data-card-id`). */
  id: string;
  name: string;
  /** `data-state`, for hosts and tests. */
  state: string;
  status: UploadStatus;
  subtitle: ReactNode;
  subtitleTone?: "muted" | "warn" | "danger";
  /** Left out of the upload (unsupported, failed): dimmed. */
  excluded?: boolean;
  selected?: boolean;
  /** Clicking the name previews the file; absent, the name is plain text. */
  onSelect?: () => void;
  /** Actions before Remove (Retry, Add Anyway). */
  trailing?: ReactNode;
  onRemove?: () => void;
  /** Nesting depth in a folder tree; indents the card. */
  depth?: number;
  testId?: string;
  /** Below the title row (a conflict choice). */
  children?: ReactNode;
};

/**
 * One file in an upload rail: its status, type, name (a preview button), a
 * toned subtitle and a hover Remove. Case files and the firm library both list
 * what is about to be added with it.
 */
export const UploadFileCard: FC<Props> = ({
  id,
  name,
  state,
  status,
  subtitle,
  subtitleTone = "muted",
  excluded = false,
  selected = false,
  onSelect,
  trailing,
  onRemove,
  depth = 0,
  testId = "staged-card",
  children
}) => (
  <li
    className={cn(
      "group relative rounded-lg border border-line-2 bg-elevated px-3 py-2.5 shadow-sm animate-in fade-in slide-in-from-top-2",
      selected && "border-accent-line bg-accent-soft",
      excluded && "opacity-60"
    )}
    style={depth ? { marginLeft: depth * 16 } : undefined}
    data-testid={testId}
    data-card-id={id}
    data-state={state}
    data-selected={selected || undefined}
  >
    <div className="flex items-center gap-3">
      <span className="flex size-6 shrink-0 items-center justify-center">
        <UploadStatusIcon status={status} />
      </span>
      <FileTypeIcon name={name} size={16} />
      <div className="min-w-0 flex-1">
        <button
          type="button"
          className={cn(
            "block max-w-full truncate text-left text-base font-medium text-fg focus-visible:outline-none",
            onSelect
              ? "cursor-pointer rounded-sm after:absolute after:inset-0 after:rounded-lg focus-visible:ring-2 focus-visible:ring-accent-line"
              : "cursor-default"
          )}
          title={name}
          disabled={!onSelect}
          aria-pressed={onSelect ? selected : undefined}
          aria-label={onSelect ? `Preview ${name}` : undefined}
          onClick={onSelect}
          data-testid={`${testId}-preview`}
        >
          {name}
        </button>
        <div
          className={cn(
            "relative z-10 truncate text-sm",
            subtitleTone === "danger"
              ? "text-danger"
              : subtitleTone === "warn"
              ? "text-warn"
              : "text-fg-3"
          )}
        >
          {subtitle}
        </div>
      </div>
      <div className="relative z-10 flex shrink-0 items-center gap-1">
        {trailing}
        {onRemove ? (
          <Button
            variant="ghost"
            size="icon-xs"
            className="opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
            aria-label={`Remove ${name}`}
            onClick={onRemove}
          >
            <Trash2 className="size-3.5" />
          </Button>
        ) : null}
      </div>
    </div>
    {children}
  </li>
);
